import { useState, useCallback, useRef } from "react";
import { CapacitorHttp } from "@capacitor/core";
import recordingStorageService from "../services/recordingStorageService";

/**
 * Convert a Supabase public URL to a blob URL on native platforms.
 * Android WebView can't load Supabase URLs directly in WaveSurfer (CORS).
 * Same approach as AudioPlayer.js uses for beat audio files.
 */
const fetchSupabaseBlobUrl = async (url) => {
  const isCapacitor = window.Capacitor !== undefined;
  const isNativePlatform = window.Capacitor?.isNativePlatform?.() || false;

  if (isCapacitor && isNativePlatform) {
    const httpResponse = await CapacitorHttp.request({
      url,
      method: "GET",
      responseType: "blob",
    });
    if (httpResponse.status !== 200)
      throw new Error(`HTTP ${httpResponse.status}`);
    if (!httpResponse.data) throw new Error("No data in response");

    // Detect MIME type from response headers or URL extension
    const contentType =
      httpResponse.headers?.["content-type"] ||
      httpResponse.headers?.["Content-Type"] ||
      (url.includes(".mp4") ? "audio/mp4" : "audio/webm");

    let blob;
    if (typeof httpResponse.data === "string") {
      const byteCharacters = atob(httpResponse.data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      blob = new Blob([new Uint8Array(byteNumbers)], { type: contentType });
    } else {
      blob = new Blob([httpResponse.data], { type: contentType });
    }
    return URL.createObjectURL(blob);
  } else {
    const response = await fetch(url, { mode: "cors", credentials: "omit" });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const blob = await response.blob();
    return URL.createObjectURL(blob);
  }
};

/**
 * Hook for managing the CRUD lifecycle of saved takes for a song.
 * Handles listing, saving, deleting, renaming takes, and managing playback URLs.
 */
const useTakeManager = ({
  storageType = "local",
  userId,
  isAuthenticated,
} = {}) => {
  const [takes, setTakes] = useState([]);
  const [isLoadingTakes, setIsLoadingTakes] = useState(false);
  const [activeTakeNumber, setActiveTakeNumber] = useState(null);
  const [activeSongId, setActiveSongId] = useState(null);
  const blobUrlsRef = useRef(new Map());

  /**
   * Load takes for a song
   */
  const loadTakes = useCallback(
    async (songId) => {
      if (!songId) return;
      setIsLoadingTakes(true);
      setActiveSongId(songId);

      try {
        const list = await recordingStorageService.listRecordings(
          songId,
          userId,
          storageType,
        );
        setTakes(list);

        // Auto-select the first take if none selected
        if (list.length > 0 && !activeTakeNumber) {
          setActiveTakeNumber(list[0].takeNumber);
        }
      } catch (error) {
        console.error("❌ Error loading takes:", error);
        setTakes([]);
      } finally {
        setIsLoadingTakes(false);
      }
    },
    [storageType, userId, activeTakeNumber],
  );

  /**
   * Save a new take from a recording blob
   */
  const saveTake = useCallback(
    async (songId, audioBlob, label, duration) => {
      if (!songId || !audioBlob) return null;

      try {
        const recording = await recordingStorageService.uploadRecording(
          audioBlob,
          songId,
          userId,
          storageType,
          { label, duration },
        );

        // Refresh take list
        await loadTakes(songId);

        // Select the new take
        setActiveTakeNumber(recording.takeNumber);

        console.log("✅ Take saved:", recording.label);
        return recording;
      } catch (error) {
        console.error("❌ Error saving take:", error);
        throw error;
      }
    },
    [storageType, userId, loadTakes],
  );

  /**
   * Delete a take
   */
  const deleteTake = useCallback(
    async (recording) => {
      if (!recording) return;

      try {
        await recordingStorageService.deleteRecording(
          recording,
          userId,
          storageType,
        );

        // Revoke blob URL if cached
        const urlKey = `${recording.songId}-${recording.takeNumber}`;
        if (blobUrlsRef.current.has(urlKey)) {
          URL.revokeObjectURL(blobUrlsRef.current.get(urlKey));
          blobUrlsRef.current.delete(urlKey);
        }

        // Refresh take list
        await loadTakes(recording.songId);

        // If we deleted the active take, select another
        if (activeTakeNumber === recording.takeNumber) {
          setActiveTakeNumber(null);
        }

        console.log("✅ Take deleted:", recording.label);
      } catch (error) {
        console.error("❌ Error deleting take:", error);
        throw error;
      }
    },
    [storageType, userId, loadTakes, activeTakeNumber],
  );

  /**
   * Rename a take
   */
  const renameTake = useCallback(
    async (recording, newLabel) => {
      if (!recording || !newLabel) return;

      try {
        await recordingStorageService.renameRecording(
          recording,
          newLabel,
          userId,
          storageType,
        );

        // Update in local state
        setTakes((prev) =>
          prev.map((t) =>
            t.takeNumber === recording.takeNumber
              ? { ...t, label: newLabel }
              : t,
          ),
        );

        console.log("✅ Take renamed to:", newLabel);
      } catch (error) {
        console.error("❌ Error renaming take:", error);
        throw error;
      }
    },
    [storageType, userId],
  );

  /**
   * Get the playback URL for a take (caches blob URLs for local takes)
   */
  const getTakeUrl = useCallback(
    async (recording) => {
      if (!recording) return null;

      const urlKey = `${recording.songId}-${recording.takeNumber}`;

      // Return cached blob URL if available
      if (blobUrlsRef.current.has(urlKey)) {
        return blobUrlsRef.current.get(urlKey);
      }

      try {
        let url = await recordingStorageService.getRecordingUrl(
          recording,
          storageType,
        );

        // On native platforms, convert Supabase URLs to blob URLs (CORS workaround)
        if (url && url.includes("supabase.co")) {
          try {
            const blobUrl = await fetchSupabaseBlobUrl(url);
            blobUrlsRef.current.set(urlKey, blobUrl);
            return blobUrl;
          } catch (fetchErr) {
            console.warn("⚠️ Failed to fetch take blob URL:", fetchErr);
            // Fall through to return raw URL as last resort
          }
        }

        // Cache blob URLs (local mode)
        if (url && url.startsWith("blob:")) {
          blobUrlsRef.current.set(urlKey, url);
        }

        return url;
      } catch (error) {
        console.error("❌ Error getting take URL:", error);
        return null;
      }
    },
    [storageType],
  );

  /**
   * Update the shift offset for a take
   */
  const updateShiftOffset = useCallback(
    async (recording, shiftOffsetSec) => {
      if (!recording) return;

      try {
        await recordingStorageService.updateShiftOffset(
          recording,
          shiftOffsetSec,
          userId,
          storageType,
        );

        // Update local state
        setTakes((prev) =>
          prev.map((t) =>
            t.takeNumber === recording.takeNumber
              ? { ...t, shiftOffsetSec }
              : t,
          ),
        );

        console.log("✅ Shift offset updated:", shiftOffsetSec);
      } catch (error) {
        console.error("❌ Error updating shift offset:", error);
        throw error;
      }
    },
    [storageType, userId],
  );

  /**
   * Replace the audio blob for an existing take (used by punch-in splice)
   */
  const replaceTakeAudio = useCallback(
    async (recording, newBlob, duration) => {
      if (!recording || !newBlob) return;

      try {
        await recordingStorageService.replaceRecordingAudio(
          recording,
          newBlob,
          duration,
          userId,
          storageType,
        );

        // Invalidate cached blob URL so WaveSurfer reloads fresh audio
        const urlKey = `${recording.songId}-${recording.takeNumber}`;
        if (blobUrlsRef.current.has(urlKey)) {
          URL.revokeObjectURL(blobUrlsRef.current.get(urlKey));
          blobUrlsRef.current.delete(urlKey);
        }

        // Reload takes list to pick up new duration/size
        await loadTakes(recording.songId);

        console.log("✅ Take audio replaced:", recording.label);
      } catch (error) {
        console.error("❌ Error replacing take audio:", error);
        throw error;
      }
    },
    [storageType, userId, loadTakes],
  );

  /**
   * Get the currently active take object
   */
  const getActiveTake = useCallback(() => {
    if (!activeTakeNumber) return null;
    return takes.find((t) => t.takeNumber === activeTakeNumber) || null;
  }, [takes, activeTakeNumber]);

  /**
   * Revoke all cached blob URLs (call on cleanup)
   */
  const revokeAllUrls = useCallback(() => {
    blobUrlsRef.current.forEach((url) => {
      if (url.startsWith("blob:")) {
        URL.revokeObjectURL(url);
      }
    });
    blobUrlsRef.current.clear();
  }, []);

  return {
    // State
    takes,
    isLoadingTakes,
    activeTakeNumber,
    activeSongId,

    // Actions
    loadTakes,
    saveTake,
    deleteTake,
    renameTake,
    replaceTakeAudio,
    updateShiftOffset,
    getTakeUrl,
    setActiveTakeNumber,
    getActiveTake,
    revokeAllUrls,
  };
};

export default useTakeManager;
